import { OrderProviderExternalErrorsWidgetsNames } from 'pages/Monitoring/OrderProviderExternalErrors/constants/widgetsNames';
import { useGetOrderProviderExternalErrorsChartData } from 'pages/Monitoring/OrderProviderExternalErrors/hooks/useGetOrderProviderExternalErrorsChartData';
import { OrderProviderExternalErrorsFilterValues } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsFilter/OrderProviderExternalErrorsFilter';
import {
  StyledBox,
  StyledDatagrid,
  StyledDialog,
} from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsSummary/OrderProviderExternalErrorsSummary.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { FunctionField } from 'shared/react-admin/Fields';
import { getReportPeriod } from 'utils/getReportPeriod';

import { OrderProviderExternalErrorsSummaryGatewaysDialogSkeleton } from './OrderProviderExternalErrorsSummaryGatewaysDialogSkeleton';

type Props = {
  filterValues: OrderProviderExternalErrorsFilterValues;
  gatewayName: string;
  gatewayId: string;
} & DialogProps;

export const OrderProviderExternalErrorsSummaryGatewaysDialog: React.FC<Props> = ({
  filterValues,
  open,
  onClose,
  gatewayName,
  gatewayId,
}): JSX.Element => {
  const translate = useTypedTranslate();
  const { data, isLoading, error } = useGetOrderProviderExternalErrorsChartData(
    { ...filterValues, gateway: [gatewayId] },
    [OrderProviderExternalErrorsWidgetsNames.RatioByReasons],
  );
  const errors = data?.ratio_by_reasons?.[0]?.errors;

  const reportPeriod = getReportPeriod({
    dateGte: filterValues?.createdAt?.gte,
    dateLte: filterValues?.createdAt?.lte,
    timezone: filterValues?.timezone,
  });

  const summaryTitle = translate(
    'app.orderProviderExternalErrors.summaryDeclineReasonsForGateway',
    { gatewayName, reportPeriod },
  );

  return (
    <StyledDialog onClose={onClose} open={open}>
      <Box>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={summaryTitle}
          titleSize="smallBold"
        />
        <CardContent>
          <StyledBox>
            {isLoading ? (
              <OrderProviderExternalErrorsSummaryGatewaysDialogSkeleton />
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <StyledDatagrid
                bulkActionButtons={false}
                data={errors}
                empty={<></>}
                isLoading={isLoading}
              >
                <FunctionField
                  label={translate('app.orderProviderExternalErrors.datagrid.declineReason')}
                  render={(record) => record.error}
                />
                <FunctionField
                  label={translate(
                    'app.orderProviderExternalErrors.datagrid.numberOfOrdersWithReason',
                  )}
                  render={(record) => record.errorsCount}
                />
                <FunctionField
                  label={translate(
                    'app.orderProviderExternalErrors.datagrid.percentOfDeclinesForSelectedPeriod',
                  )}
                  render={(record) => record.percentageRatio}
                />
              </StyledDatagrid>
            )}
          </StyledBox>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={
            <Button label={translate('app.buttons.close')} onClick={onClose} variant="outlined" />
          }
        />
      </Box>
    </StyledDialog>
  );
};
